// DEPENDENCIES ---------------------------------------------------------------- //

import { css } from '@emotion/css';
import { Skeleton, Theme, Typography, useTheme } from '@mui/material';
import { PiSteeringWheelFill } from 'react-icons/pi';
import {
  BiSolidGroup,
  BiSolidUserBadge,
} from 'react-icons/bi';

// TYPES ---------------------------------------------------------------- //

export type DriverAccessMetric = 'all' | 'driver-only' | 'portal';

interface DriverMetricsProps {
  allCount: number;
  driverOnlyCount: number;
  portalCount: number;
  metric: DriverAccessMetric;
  onMetricChange: (metric: DriverAccessMetric) => void;
  loading?: boolean;
}

// COMPONENT ---------------------------------------------------------------- //

const DriverMetrics = ({ allCount, driverOnlyCount, portalCount, metric, onMetricChange, loading }: DriverMetricsProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const clsx = useSxStyles(theme);

  const cards: { key: DriverAccessMetric; label: string; count: number; icon: React.ReactNode; activeClass: string }[] = [
    {
      key: 'all',
      label: 'All Drivers',
      count: allCount,
      icon: <BiSolidGroup size={40} color={theme.palette.utility.main} />,
      activeClass: cls.metricCardAllActive,
    },
    {
      key: 'driver-only',
      label: 'Driver Only',
      count: driverOnlyCount,
      icon: <PiSteeringWheelFill size={40} color={theme.palette.info.main} />,
      activeClass: cls.metricCardDriverOnlyActive,
    },
    {
      key: 'portal',
      label: 'Driver + Portal',
      count: portalCount,
      icon: <BiSolidUserBadge size={40} color={theme.palette.warning.main} />,
      activeClass: cls.metricCardPortalActive,
    },
  ];

  return (
    <div className={cls.metrics}>
      {cards.map(card => (
        <div
          key={card.key}
          className={`${cls.metricCard} ${metric === card.key ? card.activeClass : ''}`}
          onClick={() => onMetricChange(card.key)}
        >
          <div className={cls.metricContent}>
            {card.icon}
            <div>
              {loading ? (
                <Skeleton width={48} height={36} />
              ) : (
                <Typography variant="h3" sx={clsx.metricNumber}>
                  {card.count}
                </Typography>
              )}
              <Typography variant="body2" sx={clsx.metricLabel}>
                {card.label}
              </Typography>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  metrics: css`
    display: flex;
    gap: 16px;
  `,

  metricCard: css`
    flex: 1;
    padding: 24px;
    border: 1px solid ${theme.palette.divider};
    border-radius: 10px;
    outline: 2px solid transparent;
    outline-offset: -2px;
    background-color: ${theme.palette.background.default};
    cursor: pointer;
    transition: all 0.15s ease-in-out;

    &:hover {
      background-color: ${theme.palette.background.alt};
    }
  `,

  metricCardAllActive: css`
    outline-color: ${theme.palette.utility.main};
    background-color: ${theme.palette.utility.main}20;
    &:hover {
      background-color: ${theme.palette.utility.main}30;
    }
  `,
  metricCardDriverOnlyActive: css`
    outline-color: ${theme.palette.info.main};
    background-color: ${theme.palette.info.main}20;
    &:hover {
      background-color: ${theme.palette.info.main}30;
    }
  `,
  metricCardPortalActive: css`
    outline-color: ${theme.palette.warning.main};
    background-color: ${theme.palette.warning.main}20;
    &:hover {
      background-color: ${theme.palette.warning.main}30;
    }
  `,

  metricContent: css`
    display: flex;
    align-items: center;
    gap: 16px;
  `,
});

const useSxStyles = (theme: Theme) => ({
  metricNumber: {
    lineHeight: 1,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  metricLabel: {
    lineHeight: 1,
    marginTop: '4px',
    color: theme.palette.text.secondary,
  },
});

// EXPORT ---------------------------------------------------------------- //

export default DriverMetrics;
